/**
 * Achievement definitions - 50 total achievements
 * All text in English
 */

import type { Difficulty } from '../types';

// Stats interface for achievement checking
export interface AchievementStats {
    totalDeaths: number;
    totalPlaytime: number;
    totalScore: number;
    totalEvolutions: number;
    totalShieldsUsed: number;
    totalGamesPlayed: number;
    totalHeartsCollected: number;
    totalStarsCollected: number;
    session: {
        survivalTime: number;
        score: number;
        reachedMaxStage: boolean;
        noHitStreak: number;
        heartsCollected: number;
        starsCollected: number;
        difficulty: Difficulty;
        bigballMode: boolean;
        bossDefeated: boolean;
        survivedAfterDevolve: boolean;
    };
}

export interface Achievement {
    id: string;
    name: string;
    description: string;
    category: string;
    check: (stats: AchievementStats) => boolean;
}

export const AchievementCategory = {
    SURVIVAL: 'survival',
    DEATH: 'death',
    SCORE: 'score',
    PLAYTIME: 'playtime',
    CUMULATIVE: 'cumulative',
    SPECIAL: 'special'
} as const;

export type AchievementCategoryType = typeof AchievementCategory[keyof typeof AchievementCategory];

export const ACHIEVEMENTS: Record<string, Achievement> = {
    // ========== SURVIVAL TIME (8) ==========
    survival_30: {
        id: 'survival_30',
        name: 'First Steps',
        description: 'Survive 30 seconds',
        category: AchievementCategory.SURVIVAL,
        check: (stats: AchievementStats) => stats.session.survivalTime >= 30
    },
    survival_60: {
        id: 'survival_60',
        name: 'Getting Started',
        description: 'Survive 1 minute',
        category: AchievementCategory.SURVIVAL,
        check: (stats: AchievementStats) => stats.session.survivalTime >= 60
    },
    survival_120: {
        id: 'survival_120',
        name: 'Two Minute Hero',
        description: 'Survive 2 minutes',
        category: AchievementCategory.SURVIVAL,
        check: (stats: AchievementStats) => stats.session.survivalTime >= 120
    },
    survival_180: {
        id: 'survival_180',
        name: 'Endurance Test',
        description: 'Survive 3 minutes',
        category: AchievementCategory.SURVIVAL,
        check: (stats: AchievementStats) => stats.session.survivalTime >= 180
    },
    survival_300: {
        id: 'survival_300',
        name: 'Five Minute Master',
        description: 'Survive 5 minutes',
        category: AchievementCategory.SURVIVAL,
        check: (stats: AchievementStats) => stats.session.survivalTime >= 300
    },
    survival_420: {
        id: 'survival_420',
        name: 'Seven Minute Legend',
        description: 'Survive 7 minutes',
        category: AchievementCategory.SURVIVAL,
        check: (stats: AchievementStats) => stats.session.survivalTime >= 420
    },
    survival_600: {
        id: 'survival_600',
        name: 'Ten Minute Champion',
        description: 'Survive 10 minutes',
        category: AchievementCategory.SURVIVAL,
        check: (stats: AchievementStats) => stats.session.survivalTime >= 600
    },
    survival_900: {
        id: 'survival_900',
        name: 'Quarter Hour God',
        description: 'Survive 15 minutes',
        category: AchievementCategory.SURVIVAL,
        check: (stats: AchievementStats) => stats.session.survivalTime >= 900
    },

    // ========== DEATH COUNT (6) ==========
    death_3: {
        id: 'death_3',
        name: 'First Falls',
        description: 'Die 3 times',
        category: AchievementCategory.DEATH,
        check: (stats: AchievementStats) => stats.totalDeaths >= 3
    },
    death_10: {
        id: 'death_10',
        name: 'Learning Curve',
        description: 'Die 10 times',
        category: AchievementCategory.DEATH,
        check: (stats: AchievementStats) => stats.totalDeaths >= 10
    },
    death_50: {
        id: 'death_50',
        name: 'Never Give Up',
        description: 'Die 50 times',
        category: AchievementCategory.DEATH,
        check: (stats: AchievementStats) => stats.totalDeaths >= 50
    },
    death_100: {
        id: 'death_100',
        name: 'Determined',
        description: 'Die 100 times',
        category: AchievementCategory.DEATH,
        check: (stats: AchievementStats) => stats.totalDeaths >= 100
    },
    death_300: {
        id: 'death_300',
        name: 'Battle Scarred',
        description: 'Die 300 times',
        category: AchievementCategory.DEATH,
        check: (stats: AchievementStats) => stats.totalDeaths >= 300
    },
    death_500: {
        id: 'death_500',
        name: 'Veteran',
        description: 'Die 500 times',
        category: AchievementCategory.DEATH,
        check: (stats: AchievementStats) => stats.totalDeaths >= 500
    },

    // ========== SCORE MILESTONES (6) ==========
    score_100: {
        id: 'score_100',
        name: 'First Century',
        description: 'Score 100 points',
        category: AchievementCategory.SCORE,
        check: (stats: AchievementStats) => stats.session.score >= 100
    },
    score_300: {
        id: 'score_300',
        name: 'Triple Digits Pro',
        description: 'Score 300 points',
        category: AchievementCategory.SCORE,
        check: (stats: AchievementStats) => stats.session.score >= 300
    },
    score_500: {
        id: 'score_500',
        name: 'Half a Thousand',
        description: 'Score 500 points',
        category: AchievementCategory.SCORE,
        check: (stats: AchievementStats) => stats.session.score >= 500
    },
    score_1000: {
        id: 'score_1000',
        name: 'Thousand Club',
        description: 'Score 1000 points',
        category: AchievementCategory.SCORE,
        check: (stats: AchievementStats) => stats.session.score >= 1000
    },
    score_2000: {
        id: 'score_2000',
        name: 'Two Thousand',
        description: 'Score 2000 points',
        category: AchievementCategory.SCORE,
        check: (stats: AchievementStats) => stats.session.score >= 2000
    },
    score_5000: {
        id: 'score_5000',
        name: 'Five Thousand',
        description: 'Score 5000 points',
        category: AchievementCategory.SCORE,
        check: (stats: AchievementStats) => stats.session.score >= 5000
    },

    // ========== TOTAL PLAYTIME (5) ==========
    playtime_5m: {
        id: 'playtime_5m',
        name: 'Quick Session',
        description: 'Play 5 minutes total',
        category: AchievementCategory.PLAYTIME,
        check: (stats: AchievementStats) => stats.totalPlaytime >= 5 * 60
    },
    playtime_30m: {
        id: 'playtime_30m',
        name: 'Half Hour',
        description: 'Play 30 minutes total',
        category: AchievementCategory.PLAYTIME,
        check: (stats: AchievementStats) => stats.totalPlaytime >= 30 * 60
    },
    playtime_1h: {
        id: 'playtime_1h',
        name: 'Hour Player',
        description: 'Play 1 hour total',
        category: AchievementCategory.PLAYTIME,
        check: (stats: AchievementStats) => stats.totalPlaytime >= 60 * 60
    },
    playtime_3h: {
        id: 'playtime_3h',
        name: 'Dedicated',
        description: 'Play 3 hours total',
        category: AchievementCategory.PLAYTIME,
        check: (stats: AchievementStats) => stats.totalPlaytime >= 3 * 60 * 60
    },
    playtime_6h: {
        id: 'playtime_6h',
        name: 'Devoted',
        description: 'Play 6 hours total',
        category: AchievementCategory.PLAYTIME,
        check: (stats: AchievementStats) => stats.totalPlaytime >= 6 * 60 * 60
    },

    // ========== CUMULATIVE SCORE (5) ==========
    cumulative_1000: {
        id: 'cumulative_1000',
        name: 'Thousand Total',
        description: 'Earn 1,000 total points',
        category: AchievementCategory.CUMULATIVE,
        check: (stats: AchievementStats) => stats.totalScore >= 1000
    },
    cumulative_5000: {
        id: 'cumulative_5000',
        name: 'Five Thousand Total',
        description: 'Earn 5,000 total points',
        category: AchievementCategory.CUMULATIVE,
        check: (stats: AchievementStats) => stats.totalScore >= 5000
    },
    cumulative_10000: {
        id: 'cumulative_10000',
        name: 'Ten Thousand Total',
        description: 'Earn 10,000 total points',
        category: AchievementCategory.CUMULATIVE,
        check: (stats: AchievementStats) => stats.totalScore >= 10000
    },
    cumulative_50000: {
        id: 'cumulative_50000',
        name: 'Fifty Thousand Total',
        description: 'Earn 50,000 total points',
        category: AchievementCategory.CUMULATIVE,
        check: (stats: AchievementStats) => stats.totalScore >= 50000
    },
    cumulative_100000: {
        id: 'cumulative_100000',
        name: 'Hundred Thousand',
        description: 'Earn 100,000 total points',
        category: AchievementCategory.CUMULATIVE,
        check: (stats: AchievementStats) => stats.totalScore >= 100000
    },

    // ========== SPECIAL (18) ==========
    evolve_3: {
        id: 'evolve_3',
        name: 'Evolution Beginner',
        description: 'Evolve 3 times total',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.totalEvolutions >= 3
    },
    evolve_10: {
        id: 'evolve_10',
        name: 'Evolution Expert',
        description: 'Evolve 10 times total',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.totalEvolutions >= 10
    },
    evolve_max: {
        id: 'evolve_max',
        name: 'Final Form',
        description: 'Reach hexagon stage',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.reachedMaxStage
    },
    shield_use_5: {
        id: 'shield_use_5',
        name: 'Shield Master',
        description: 'Use shields 5 times total',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.totalShieldsUsed >= 5
    },
    shield_use_20: {
        id: 'shield_use_20',
        name: 'Shield Expert',
        description: 'Use shields 20 times total',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.totalShieldsUsed >= 20
    },
    games_5: {
        id: 'games_5',
        name: 'Getting Hooked',
        description: 'Play 5 games',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.totalGamesPlayed >= 5
    },
    games_25: {
        id: 'games_25',
        name: 'Regular Player',
        description: 'Play 25 games',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.totalGamesPlayed >= 25
    },
    games_100: {
        id: 'games_100',
        name: 'Centurion',
        description: 'Play 100 games',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.totalGamesPlayed >= 100
    },
    no_hit_30: {
        id: 'no_hit_30',
        name: 'Untouchable',
        description: '30 seconds without hit',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.noHitStreak >= 30
    },
    no_hit_60: {
        id: 'no_hit_60',
        name: 'Ghost',
        description: '60 seconds without hit',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.noHitStreak >= 60
    },
    collect_hearts_10: {
        id: 'collect_hearts_10',
        name: 'Heart Collector',
        description: 'Collect 10 hearts in one game',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.heartsCollected >= 10
    },
    collect_stars_5: {
        id: 'collect_stars_5',
        name: 'Star Gatherer',
        description: 'Collect 5 stars in one game',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.starsCollected >= 5
    },
    hard_mode_60: {
        id: 'hard_mode_60',
        name: 'Hard Mode Hero',
        description: 'Survive 60s on hard',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.difficulty === 'hard' && stats.session.survivalTime >= 60
    },
    hard_mode_180: {
        id: 'hard_mode_180',
        name: 'Hard Mode Legend',
        description: 'Survive 180s on hard',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.difficulty === 'hard' && stats.session.survivalTime >= 180
    },
    easy_mode_300: {
        id: 'easy_mode_300',
        name: 'Easy Cruiser',
        description: 'Survive 300s on easy',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.difficulty === 'easy' && stats.session.survivalTime >= 300
    },
    bigball_mode_60: {
        id: 'bigball_mode_60',
        name: 'Big Ball Survivor',
        description: 'Survive 60s on BigBall mode',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.bigballMode && stats.session.survivalTime >= 60
    },
    boss_survive: {
        id: 'boss_survive',
        name: 'Boss Survivor',
        description: 'Survive a boss encounter',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.bossDefeated
    },
    comeback: {
        id: 'comeback',
        name: 'Comeback Kid',
        description: 'Survive after devolving',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.session.survivedAfterDevolve
    },

    // ========== BGM UNLOCK (2) ==========
    bgm_memorial_city: {
        id: 'bgm_memorial_city',
        name: 'City Memory',
        description: 'Play 10 games',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.totalGamesPlayed >= 10
    },
    bgm_neon_lights: {
        id: 'bgm_neon_lights',
        name: 'Neon Runner',
        description: 'Score 2000 points total',
        category: AchievementCategory.SPECIAL,
        check: (stats: AchievementStats) => stats.totalScore >= 2000
    }
};

// Get total achievement count
export const TOTAL_ACHIEVEMENTS: number = Object.keys(ACHIEVEMENTS).length;

// Get all achievements as array
export function getAllAchievements(): Achievement[] {
    return Object.values(ACHIEVEMENTS);
}
